import React, { useState, useEffect, useCallback } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { relationshipAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Users, Phone, MapPin, Search, UserPlus, Loader2, AlertTriangle } from 'lucide-react';

const FIND_FARMERS = '/customers/find';

/**
 * A shopkeeper's customers: farmers they have an accepted connection
 * with, in either direction. Phone numbers are shown because these are
 * accepted connections who arrange orders off-platform.
 */
export default function CustomersPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const loadCustomers = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await relationshipAPI.getConnectedUsers(user.id, { direction: 'any' });
      const rows = Array.isArray(res?.data) ? res.data : [];
      // A distributor's customers are shops and direct (farmer) customers
      // (beta feedback); a shopkeeper's are farmers.
      const kinds = user.user_type === 'distributor' ? ['farmer', 'shopkeeper'] : ['farmer'];
      setCustomers(rows.filter((u) => kinds.includes(u.user_type)));
    } catch {
      setCustomers([]);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [user.id, user.user_type]);

  useEffect(() => { void loadCustomers(); }, [loadCustomers]);

  // Home search links an unconnected farmer to /customers?invite=<name>;
  // inviting happens in the farmer directory.
  const inviteName = searchParams.get('invite');
  if (inviteName) {
    return <Navigate to={`${FIND_FARMERS}?invite=${encodeURIComponent(inviteName)}`} replace />;
  }

  const q = searchQuery.trim().toLowerCase();
  const filteredCustomers = customers.filter((c) =>
    !q
    || c.full_name?.toLowerCase().includes(q)
    || c.location?.toLowerCase().includes(q)
    || c.phone?.includes(q),
  );

  const renderBody = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-farm-primary" />
        </div>
      );
    }
    if (error) {
      return (
        <Card className="border border-amber-300 bg-amber-50" data-testid="customers-error">
          <CardContent className="p-6 flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-amber-900">{t('customers.error.title')}</h3>
              <p className="text-sm text-amber-800 mt-1">{t('customers.error.body')}</p>
              <Button variant="outline" size="sm" className="mt-3" onClick={() => void loadCustomers()}>
                {t('customers.error.retry')}
              </Button>
            </div>
          </CardContent>
        </Card>
      );
    }
    if (customers.length === 0) {
      return (
        <Card data-testid="customers-empty">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Users className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold mb-2">{t('customers.empty.title')}</h3>
            <p className="text-muted-foreground text-center mb-4">{t('customers.empty.subtitle')}</p>
            <Button className="bg-farm-primary" onClick={() => navigate(FIND_FARMERS)}>
              <UserPlus className="w-4 h-4 mr-2" /> {t('customers.add')}
            </Button>
          </CardContent>
        </Card>
      );
    }
    if (filteredCustomers.length === 0) {
      return <p className="text-sm text-muted-foreground text-center py-8">{t('customers.noMatches')}</p>;
    }
    return (
      <div className="space-y-3">
        {filteredCustomers.map((customer) => (
          <Card key={customer.id} data-testid="customer-row">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-farm-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Users className="w-6 h-6 text-farm-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold truncate">{customer.full_name}</h3>
                  {user.user_type === 'distributor' && (
                    <span className="text-xs font-semibold text-farm-primary" data-testid="customer-kind">
                      {customer.user_type === 'shopkeeper' ? t('customers.kind.shop') : t('customers.kind.direct')}
                    </span>
                  )}
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground mt-1">
                    {customer.phone && (
                      <a
                        href={`tel:${customer.phone}`}
                        className="flex items-center gap-1 text-farm-primary hover:underline"
                        aria-label={t('customers.call', { name: customer.full_name })}
                      >
                        <Phone className="w-3 h-3" />
                        {customer.phone}
                      </a>
                    )}
                    {customer.location && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {customer.location}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  };

  return (
    <div className="p-4 animate-fadeIn">
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} aria-label={t('common.back', { defaultValue: 'Back' })}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="font-heading text-2xl font-bold">{t('customers.title')}</h1>
          {!loading && !error && (
            <p className="text-muted-foreground text-sm">{t('customers.count', { count: customers.length })}</p>
          )}
        </div>
        <Button size="sm" className="bg-farm-primary" onClick={() => navigate(FIND_FARMERS)} data-testid="customers-invite">
          <UserPlus className="w-4 h-4 mr-1" /> {t('customers.add')}
        </Button>
      </div>

      {customers.length > 0 && (
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder={t('customers.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      )}

      {renderBody()}
    </div>
  );
}
